import React from 'react';
import { Link } from 'react-router-dom';
import { Filter, Search, ChevronRight, Plus, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { matters as mattersApi } from '@/lib/api';

const STAGE_COLORS = {
  INTAKE: 'bg-slate-100 text-slate-700',
  ENGAGED: 'bg-blue-100 text-blue-700',
  IN_PREP: 'bg-amber-100 text-amber-700',
  LODGED: 'bg-indigo-100 text-indigo-700',
  DECIDED: 'bg-emerald-100 text-emerald-700',
};
const RAG_COLORS = { GREEN: 'bg-emerald-500', AMBER: 'bg-amber-500', RED: 'bg-rose-500' };
const STAGES = ['ALL', 'INTAKE', 'ENGAGED', 'IN_PREP', 'LODGED', 'DECIDED'];

export default function MattersListPage() {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [stage, setStage] = React.useState('ALL');
  const [q, setQ] = React.useState('');
  const [includeArchived, setIncludeArchived] = React.useState(false);

  const load = () => {
    setLoading(true);
    mattersApi.list().then(setRows).finally(() => setLoading(false));
  };
  React.useEffect(load, []);

  const filtered = rows.filter((m) => {
    if (!includeArchived && m.archived) return false;
    const stageOk = stage === 'ALL' || m.stage === stage;
    const qOk = !q || `${m.applicant} ${m.ref} ${m.subclass} ${m.occupation || ''}`.toLowerCase().includes(q.toLowerCase());
    return stageOk && qOk;
  });

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Matters</h1>
          <div className="text-sm text-slate-500">{rows.length} total · {filtered.length} shown</div>
        </div>
        <NewMatterDialog onCreated={load} />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[280px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by applicant, reference, subclass, occupation…" className="h-10 pl-9" />
        </div>
        <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white p-1">
          {STAGES.map((s) => (
            <button key={s} onClick={() => setStage(s)}
              className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${stage === s ? 'bg-[#1A3A5C] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              {s.replace('_', ' ')}
            </button>
          ))}
        </div>
        <label className="ml-2 flex items-center gap-1.5 text-xs text-slate-600">
          <input data-testid="matters-include-archived" type="checkbox"
                 checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Include archived
        </label>
        <Button variant="outline" className="h-10 gap-2 border-slate-200"><Filter className="h-4 w-4" /> More filters</Button>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-slate-200 bg-white">
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-3 text-left">Applicant</th>
                <th className="px-4 py-3 text-left">Reference</th>
                <th className="px-4 py-3 text-left">Subclass</th>
                <th className="px-4 py-3 text-left">Occupation</th>
                <th className="px-4 py-3 text-left">Stage</th>
                <th className="px-4 py-3 text-left">Next deadline</th>
                <th className="px-4 py-3 text-right">Trust</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((m) => (
                <tr key={m.id} className="transition-colors hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`h-2.5 w-2.5 rounded-full ${RAG_COLORS[m.rag] || 'bg-slate-300'}`} />
                      <div>
                        <Link to={`/app/matters/${m.id}`} className="font-medium text-[#1A3A5C] hover:text-[#2ECC9A]">{m.applicant}</Link>
                        <div className="text-xs text-slate-500">{m.country || '—'}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{m.ref}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1A3A5C]">{m.subclass}</div>
                    {m.stream && <div className="text-xs text-slate-500">{m.stream}</div>}
                  </td>
                  <td className="px-4 py-3 text-xs">
                    {m.occupation ? (<><div className="font-medium text-[#1A3A5C]">{m.occupation}</div><div className="text-slate-500">via {m.assessing_body}</div></>) : (<span className="text-slate-400">—</span>)}
                  </td>
                  <td className="px-4 py-3">
                    <Badge className={(STAGE_COLORS[m.stage] || 'bg-slate-100 text-slate-700') + ' text-[10px] uppercase tracking-wider'}>{(m.stage||'').replace('_',' ')}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <div className="font-medium text-[#1A3A5C]">{m.next_deadline || '—'}</div>
                    <div className="text-slate-500">{m.deadline_label || ''}</div>
                  </td>
                  <td className="px-4 py-3 text-right text-xs">
                    {m.trust_balance > 0 ? <span className="font-mono font-medium text-[#1A3A5C]">A${m.trust_balance.toLocaleString()}</span> : <span className="text-slate-400">A$0</span>}
                  </td>
                  <td className="pr-4"><Link to={`/app/matters/${m.id}`} className="inline-flex"><ChevronRight className="h-4 w-4 text-slate-300" /></Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function NewMatterDialog({ onCreated }) {
  const [open, setOpen] = React.useState(false);
  const [f, setF] = React.useState({ applicant: '', applicant_email: '', country: '', subclass: '186', stream: 'Direct Entry', occupation: '', assessing_body: '', sponsor_id: '' });
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState('');
  const [sponsorList, setSponsorList] = React.useState([]);
  const [suggestion, setSuggestion] = React.useState(null);

  const showSponsor = ['482', '186', '187'].includes(f.subclass);

  React.useEffect(() => {
    if (!open || !showSponsor) return;
    import('@/lib/api').then((mod) => {
      mod.sponsors.list().then(setSponsorList).catch(() => setSponsorList([]));
    });
  }, [open, showSponsor]);

  // Auto-suggest sponsor from applicant email + occupation + assessing body (debounced)
  React.useEffect(() => {
    if (!showSponsor) { setSuggestion(null); return; }
    const hasSignal = (f.applicant_email && f.applicant_email.includes('@'))
                       || f.occupation
                       || f.assessing_body;
    if (!hasSignal) { setSuggestion(null); return; }
    const t = setTimeout(() => {
      import('@/lib/api').then((mod) => {
        const payload = {};
        if (f.applicant_email && f.applicant_email.includes('@')) payload.email = f.applicant_email;
        if (f.occupation) {
          payload.occupation = f.occupation;
          // Also use occupation as an industry hint so the personal-email fallback
          // can match sponsors tagged with the same industry keyword.
          payload.industry = f.occupation;
        }
        mod.sponsorsExtra.suggest(payload)
          .then((r) => setSuggestion(r.matches?.[0] || null))
          .catch(() => setSuggestion(null));
      });
    }, 500);
    return () => clearTimeout(t);
  }, [f.applicant_email, f.occupation, f.assessing_body, showSponsor]);

  const applySuggestion = () => {
    if (suggestion) setF((s) => ({ ...s, sponsor_id: suggestion.sponsor_id }));
  };

  const submit = async (e) => {
    e.preventDefault(); setErr(''); setSaving(true);
    try {
      const payload = { ...f };
      if (!payload.sponsor_id) delete payload.sponsor_id;
      if (!payload.applicant_email) delete payload.applicant_email;
      await mattersApi.create(payload);
      setOpen(false); onCreated?.();
      setF({ applicant: '', applicant_email: '', country: '', subclass: '186', stream: '', occupation: '', assessing_body: '', sponsor_id: '' });
      setSuggestion(null);
    } catch (er) { setErr(er?.response?.data?.detail || 'Create failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-10 gap-2 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90" data-testid="matter-create-btn"><Plus className="h-4 w-4" /> New matter</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Create a new matter</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Applicant</Label><Input required value={f.applicant} onChange={(e)=>setF({...f, applicant: e.target.value})} className="mt-1" /></div>
            <div><Label>Applicant email</Label><Input data-testid="matter-applicant-email" value={f.applicant_email} onChange={(e)=>setF({...f, applicant_email: e.target.value})} placeholder="worker@company.com.au" className="mt-1 font-mono text-xs" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Country</Label><Input value={f.country} onChange={(e)=>setF({...f, country: e.target.value})} className="mt-1" /></div>
            <div><Label>Subclass</Label>
              <Select value={f.subclass} onValueChange={(v)=>setF({...f, subclass: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {['186','187','189','190','482','491','500','485','820','801','600'].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Occupation (optional)</Label><Input value={f.occupation} onChange={(e)=>setF({...f, occupation: e.target.value})} className="mt-1" /></div>
            <div><Label>Assessing body</Label><Input placeholder="ACS, CPA, VETASSESS…" value={f.assessing_body} onChange={(e)=>setF({...f, assessing_body: e.target.value})} className="mt-1" /></div>
          </div>
          {showSponsor && (
            <div>
              <Label>Sponsor <span className="text-xs font-normal text-slate-500">(482/186/187 employer-sponsored)</span></Label>
              {suggestion && !f.sponsor_id && (
                <div data-testid="sponsor-suggestion"
                     className="mt-1 flex items-center justify-between gap-2 rounded-md border border-[#2ECC9A]/40 bg-[#2ECC9A]/5 p-2 text-xs">
                  <div className="text-[#0F766E]">
                    Suggested: <b>{suggestion.name}</b>
                    <span className="ml-1 text-slate-500">({Math.round(suggestion.score * 100)}% · {suggestion.reason})</span>
                  </div>
                  <button type="button" onClick={applySuggestion} data-testid="apply-sponsor-suggestion"
                          className="rounded-md bg-[#2ECC9A] px-2 py-1 text-[10px] font-semibold text-[#0F1F3A] hover:bg-[#2ECC9A]/90">
                    Apply
                  </button>
                </div>
              )}
              <Select value={f.sponsor_id || 'none'} onValueChange={(v)=>setF({...f, sponsor_id: v === 'none' ? '' : v})}>
                <SelectTrigger data-testid="matter-sponsor-picker" className="mt-1"><SelectValue placeholder="— none —"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— none —</SelectItem>
                  {sponsorList.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}{s.abn ? ` · ABN ${s.abn}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {sponsorList.length === 0 && (
                <div className="mt-1 text-[10px] text-slate-500">
                  No sponsors yet — <a href="/app/sponsors" className="text-[#2ECC9A] hover:underline">add one on the Sponsors page</a>.
                </div>
              )}
            </div>
          )}
          {err && <div className="rounded-md bg-red-50 p-2 text-xs text-red-700">{err}</div>}
          <DialogFooter>
            <Button type="submit" disabled={saving} className="bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90">{saving ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}Create matter</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
